import Vue from 'vue';
import qs from 'qs';
import { http } from '@/http';
import Modal, { ModalLinePrimary, ModalLineSecondary } from '@/components/Modal';
import Input from '@/components/Controls/Input';
import Button from '@/components/Controls/Button';
import Checkbox from '@/components/Controls/Checkbox';
import { CmsError } from '@/http/CmsError';
import { Airplane } from './Airplane';
import * as events from '@/metrix/events';
import { FeedbackWidgetData, FormModel, ModalData } from './types';
import './FeedbackWidget.scss';
import { RootElementAttrsHolder } from '@/plugins/RootElementAttrsPlugin';

const defaults: ModalData = {
    action: null,
    linePrimary: 'Обратный звонок',
    lineSecondary: 'Мы свяжемся с Вами в ближайшее время',
    submitButtonText: 'Отправить',
    metrix: {},
    trailer: {},
    message: false,
};

const getInitialFormModel = (): FormModel => ({
    name: null,
    phone: null,
    message: null,
    agreement: true,
});

export function createFeedbackWidget(el: Element) {
    const rootAttrs = new RootElementAttrsHolder(el);

    return new Vue({
        el,
        name: 'FeedbackWidget',
        rootAttrs,
        data(): FeedbackWidgetData {
            return {
                active: false,
                formModel: getInitialFormModel(),
                fieldErrors: {},
                formErrors: [],
                errorMessage: null,
                modalData: {} as ModalData,
                successfullySent: false,
            };
        },

        methods: {
            show(modalData?: ModalData) {
                this.modalData = Object.assign({}, defaults, modalData || {});
                this.active = true;

                this.$metrix.$emit(
                    events.OPEN,
                    this.getMetrixPayload(this.modalData.metrix[events.OPEN])
                );
            },

            close(modalData?: ModalData) {
                this.modalData = Object.assign({}, defaults, modalData || {});
                this.resetErrors();

                if (this.successfullySent) {
                    this.$metrix.$emit(
                        events.CLOSE,
                        this.getMetrixPayload(this.modalData.metrix?.[events.CLOSE])
                    );
                } else {
                    this.$metrix.$emit(
                        events.CANCEL,
                        this.getMetrixPayload(this.modalData.metrix?.[events.CANCEL])
                    );
                }

                this.active = false;

                if (this.successfullySent) {
                    this.formModel = getInitialFormModel();
                    this.successfullySent = false;
                }
            },

            async submit(e: SubmitEvent) {
                e.preventDefault();

                if (
                    !this.formModel.phone ||
                    this.formModel.phone.replace(/_/g, '').length < 18
                ) {
                    this.$set(this.fieldErrors, 'phone', 'Введите номер телефона');

                    return;
                }

                if (!this.modalData.action) {
                    console.warn(`[${this.$options.name}]: empty action string`);
                    return;
                }

                const dataMap = {
                    ...this.formModel,
                    ...this.modalData.trailer,
                    needValidation: true,
                    validationDate: this.getValidationDate(),
                };

                const data = qs.stringify(dataMap);

                this.$wait.start('formSend');

                try {
                    await http({
                        url: this.modalData.action,
                        method: 'post',
                        headers: { 'content-type': 'application/x-www-form-urlencoded' },
                        data,
                    });

                    const metrixPayload = this.getMetrixPayload(
                        this.modalData.metrix[events.SEND]
                    );

                    this.$metrix.$emit(
                        events.SEND,
                        Object.assign({}, metrixPayload, {
                            formData: dataMap, // TODO выпилить - поля формы добавляются через this.getMetrixPayload
                        })
                    );

                    this.successfullySent = true;
                } catch (error) {
                    this.$errorLog(error);
                    // сбрасываем все поля ошибок, чтобы отображались только актуальные
                    this.resetErrors();

                    if (error instanceof CmsError) {
                        const data = error.details.data || {};
                        const { fieldErrors, formErrors, message } = data;
                        this.fieldErrors = fieldErrors || {};
                        this.formErrors = formErrors || [];
                        this.errorMessage = message || null;
                    } else {
                        this.errorMessage = this.getErrorMessage(error);
                    }
                } finally {
                    this.$wait.end('formSend');
                }
            },

            getErrorMessage(error: any) {
                if (error.response) {
                    const message = error.message ? `(${error.message})` : '';
                    return `Сервер отклонил запрос ${message}`;
                } else if (error.request) {
                    return 'Сервер не отвечает';
                } else {
                    return 'Неизвестная ошибка';
                }
            },

            resetErrors() {
                this.fieldErrors = {};
                this.formErrors = [];
                this.errorMessage = null;
            },

            deleteError(errorKey: string) {
                if (this.fieldErrors[errorKey]) {
                    this.$delete(this.fieldErrors, errorKey);
                }
            },

            getMetrixPayload(input: string) {
                const $form = Object.assign({}, this.formModel, this.modalData.trailer);

                return this.$enrichVars(input || {}, { $form });
            },

            getValidationDate() {
                const date = new Date();
                const year = date.getFullYear();
                const month = date.getMonth() + 1;
                const day = date.getDate();

                return [year, month, day].map((n) => (n < 10 ? `0${n}` : n)).join('-');
            },
        },

        render(h) {
            return (
                <Modal
                    active={this.active}
                    onClose={() => this.close()}
                    swipeDownMaxDistance={150}
                >
                    <div class="feedback-container">
                        <transition name="fade">
                            {this.successfullySent && (
                                <div class="feedback-container__success">
                                    <ModalLinePrimary class="text-center">
                                        Спасибо за обращение!
                                    </ModalLinePrimary>

                                    <div class="feedback-container__airplane">
                                        <div class="feedback-container__airplane-box">
                                            <Airplane />
                                        </div>
                                    </div>

                                    <Button
                                        onClick={() => this.close()}
                                        modifiers={['primary', 'block']}
                                    >
                                        Закрыть
                                    </Button>
                                </div>
                            )}
                        </transition>

                        <div
                            class={{
                                'feedback-container__body': true,
                                'feedback-container__body--sent': this.successfullySent,
                            }}
                        >
                            <ModalLinePrimary class="text-center">
                                <span domPropsInnerHTML={this.modalData.linePrimary} />
                            </ModalLinePrimary>

                            <ModalLineSecondary class="text-center feedback-container__line-secondary">
                                <span domPropsInnerHTML={this.modalData.lineSecondary} />
                            </ModalLineSecondary>

                            <div class="feedback-container__error-pre feedback-error">
                                {this.errorMessage}
                            </div>

                            <form
                                action="/"
                                onSubmit={this.submit}
                                class="grid grid--y-default"
                            >
                                <div class="col">
                                    <Input
                                        placeholder={'Ваше имя'}
                                        vModel={this.formModel.name}
                                        error={this.fieldErrors.name}
                                        onInput={() => {
                                            this.deleteError('name');
                                        }}
                                    />
                                </div>
                                <div class="col">
                                    <Input
                                        type="tel"
                                        mask={{
                                            mask: '+7 (999) 999-99-99[9]',
                                            placeholder: '_',
                                            showMaskOnHover: false,
                                        }}
                                        placeholder={'Номер телефона'}
                                        vModel={this.formModel.phone}
                                        error={this.fieldErrors.phone}
                                        onInput={() => {
                                            this.deleteError('phone');
                                        }}
                                        inputmode="numeric"
                                    />
                                </div>
                                {this.modalData.message && (
                                    <div class="col">
                                        <Input
                                            rows={2}
                                            vModel={this.formModel.message}
                                            placeholder="Сообщение"
                                        />
                                    </div>
                                )}
                                <div class="col text-left">
                                    <Checkbox vModel={this.formModel.agreement}>
                                        согласен с условиями передачи данных
                                    </Checkbox>
                                </div>

                                <div class="feedback-error feedback-container__error-after">
                                    {[].concat(this.formErrors).join(', ')}
                                </div>

                                <div class="col">
                                    <Button
                                        modifiers={['primary', 'block']}
                                        type="submit"
                                        loading={this.$wait.waiting('formSend')}
                                        disabled={!this.formModel.agreement}
                                    >
                                        <span
                                            domPropsInnerHTML={
                                                this.modalData.submitButtonText
                                            }
                                        />
                                    </Button>
                                </div>
                            </form>
                        </div>
                    </div>
                </Modal>
            );
        },
    });
}

// export class FeedbackWidget extends Widget {
//     constructor(selector) {
//         super(selector);

//         const rootProps = {};

//         this.init({
//             name: this.constructor.name,

//             wait,

//             data: {
//                 rootProps,
//                 active: false,
//                 formModel: getInitialFormModel(),
//                 fieldErrors: {},
//                 formErrors: [],
//                 errorMessage: null,
//                 modalData: {},
//                 successfullySent: false,
//             },

//             methods: {
//                 show(modalData = {}) {
//                     this.modalData = Object.assign({}, defaults, modalData);
//                     this.active = true;

//                     this.$metrix.$emit(
//                         events.OPEN,
//                         this.getMetrixPayload(this.modalData.metrix[events.OPEN])
//                     );
//                 },

//                 close() {
//                     this.resetErrors();

//                     if (this.successfullySent) {
//                         this.$metrix.$emit(
//                             events.CLOSE,
//                             this.getMetrixPayload(this.modalData.metrix[events.CLOSE])
//                         );
//                     } else {
//                         this.$metrix.$emit(
//                             events.CANCEL,
//                             this.getMetrixPayload(this.modalData.metrix[events.CANCEL])
//                         );
//                     }

//                     this.active = false;

//                     if (this.successfullySent) {
//                         this.formModel = getInitialFormModel();
//                         this.successfullySent = false;
//                     }
//                 },

//                 async submit(e) {
//                     e.preventDefault();

//                     if (
//                         !this.formModel.phone ||
//                         this.formModel.phone.replace(/_/g, '').length < 18
//                     ) {
//                         this.$set(this.fieldErrors, 'phone', 'Введите номер телефона');

//                         return;
//                     }

//                     if (!this.modalData.action) {
//                         console.warn(`[${this.$options.name}]: empty action string`);
//                         return;
//                     }

//                     const dataMap = {
//                         ...this.formModel,
//                         ...this.modalData.trailer,
//                         needValidation: true,
//                         validationDate: this.getValidationDate(),
//                     };

//                     const data = qs.stringify(dataMap);

//                     this.$wait.start('formSend');

//                     try {
//                         await http({
//                             url: this.modalData.action,
//                             method: this.modalData.method,
//                             data,
//                             headers: {
//                                 'content-type': 'application/x-www-form-urlencoded',
//                             },
//                         });

//                         const metrixPayload = this.getMetrixPayload(
//                             this.modalData.metrix[events.SEND]
//                         );

//                         this.$metrix.$emit(
//                             events.SEND,
//                             Object.assign({}, metrixPayload, {
//                                 formData: dataMap, // TODO выпилить - поля формы добавляются через this.getMetrixPayload
//                             })
//                         );

//                         this.successfullySent = true;
//                     } catch (error) {
//                         this.$errorLog(error);
//                         // сбрасываем все поля ошибок, чтобы отображались только актуальные
//                         this.resetErrors();

//                         if (error instanceof CmsError) {
//                             const data = error.details.data || {};
//                             const { fieldErrors, formErrors, message } = data;
//                             this.fieldErrors = fieldErrors || {};
//                             this.formErrors = formErrors || [];
//                             this.errorMessage = message || null;
//                         } else {
//                             this.errorMessage = this.getErrorMessage(error);
//                         }
//                     } finally {
//                         this.$wait.end('formSend');
//                     }
//                 },

//                 getErrorMessage(error) {
//                     if (error.response) {
//                         const message = error.message ? `(${error.message})` : '';
//                         return `Сервер отклонил запрос ${message}`;
//                     } else if (error.request) {
//                         return 'Сервер не отвечает';
//                     } else {
//                         return 'Неизвестная ошибка';
//                     }
//                 },

//                 resetErrors() {
//                     this.fieldErrors = {};
//                     this.formErrors = [];
//                     this.errorMessage = null;
//                 },

//                 deleteError(errorKey) {
//                     if (this.fieldErrors[errorKey]) {
//                         this.$delete(this.fieldErrors, errorKey);
//                     }
//                 },

//                 getMetrixPayload(input) {
//                     const $form = Object.assign(
//                         {},
//                         this.formModel,
//                         this.modalData.trailer
//                     );

//                     return this.$enrichVars(input || {}, { $form });
//                 },

//                 getValidationDate() {
//                     const date = new Date();
//                     const year = date.getFullYear();
//                     const month = date.getMonth() + 1;
//                     const day = date.getDate();

//                     return [year, month, day]
//                         .map((n) => (n < 10 ? `0${n}` : n))
//                         .join('-');
//                 },
//             },

//             render(h) {
//                 return (
//                     <Modal
//                         active={this.active}
//                         onClose={() => this.close()}
//                         swipeDownMaxDistance={150}
//                     >
//                         <div class="feedback-container">
//                             <Transition name="fade">
//                                 {this.successfullySent && (
//                                     <div class="feedback-container__success">
//                                         <ModalLinePrimary class="text-center">
//                                             Спасибо за обращение!
//                                         </ModalLinePrimary>

//                                         <div class="feedback-container__airplane">
//                                             <div class="feedback-container__airplane-box">
//                                                 <Airplane />
//                                             </div>
//                                         </div>

//                                         <Button
//                                             onClick={() => this.close()}
//                                             modifiers={['primary', 'block']}
//                                         >
//                                             Закрыть
//                                         </Button>
//                                     </div>
//                                 )}
//                             </Transition>

//                             <div
//                                 class={{
//                                     'feedback-container__body': true,
//                                     'feedback-container__body--sent': this
//                                         .successfullySent,
//                                 }}
//                             >
//                                 <ModalLinePrimary class="text-center">
//                                     <span
//                                         domPropsInnerHTML={this.modalData.linePrimary}
//                                     />
//                                 </ModalLinePrimary>

//                                 <ModalLineSecondary class="text-center feedback-container__line-secondary">
//                                     <span
//                                         domPropsInnerHTML={this.modalData.lineSecondary}
//                                     />
//                                 </ModalLineSecondary>

//                                 <div class="feedback-container__error-pre feedback-error">
//                                     {this.errorMessage}
//                                 </div>

//                                 <form
//                                     action="/"
//                                     onSubmit={this.submit}
//                                     class="grid grid--y-default"
//                                 >
//                                     <div class="col">
//                                         <Input
//                                             placeholder={'Ваше имя'}
//                                             vModel={this.formModel.name}
//                                             error={this.fieldErrors.name}
//                                             onInput={() => {
//                                                 this.deleteError('name');
//                                             }}
//                                         />
//                                     </div>
//                                     <div class="col">
//                                         <Input
//                                             type="tel"
//                                             mask={{
//                                                 mask: '+7 (999) 999-99-99[9]',
//                                                 placeholder: '_',
//                                                 showMaskOnHover: false,
//                                             }}
//                                             placeholder={'Номер телефона'}
//                                             vModel={this.formModel.phone}
//                                             error={this.fieldErrors.phone}
//                                             onInput={() => {
//                                                 this.deleteError('phone');
//                                             }}
//                                             inputmode="numeric"
//                                         />
//                                     </div>
//                                     {this.modalData.message && (
//                                         <div class="col">
//                                             <Input
//                                                 rows={2}
//                                                 vModel={this.formModel.message}
//                                                 placeholder="Сообщение"
//                                             />
//                                         </div>
//                                     )}
//                                     <div class="col text-left">
//                                         <Checkbox vModel={this.formModel.agreement}>
//                                             согласен с условиями передачи данных
//                                         </Checkbox>
//                                     </div>

//                                     <div class="feedback-error feedback-container__error-after">
//                                         {[].concat(this.formErrors).join(', ')}
//                                     </div>

//                                     <div class="col">
//                                         <Button
//                                             modifiers={['primary', 'block']}
//                                             type="submit"
//                                             loading={this.$wait.waiting('formSend')}
//                                             disabled={!this.formModel.agreement}
//                                         >
//                                             <span
//                                                 domPropsInnerHTML={
//                                                     this.modalData.submitButtonText
//                                                 }
//                                             />
//                                         </Button>
//                                     </div>
//                                 </form>
//                             </div>
//                         </div>
//                     </Modal>
//                 );
//             },
//         });
//     }
// }
